import { Loader2, MonitorSmartphone, Plus, Search } from "lucide-react"
import React, { useEffect, useState } from "react"
import { useQuery } from "react-query"
import { useNavigate } from "react-router-dom"
import { Button, Modal } from "../../../components/ui"
import api from "../../../utils/axios"
import { CodeWithPairs, Pair } from "../types"
import AddDeviceForm from "./AddDeviceForm"
import PairRow from "./PairRow"

/**
 * Estado do modal, controlado pelo pai:
 * - `create`  → formulário código + link (adicionar sem sair da listagem);
 * - `inspect` → UM par (senha→link) de um code. Um code = um device; outros
 *   pares do mesmo code não aparecem aqui de propósito.
 */
export type DeviceModalState =
    | { mode: "create"; code?: string }
    | {
          mode: "inspect"
          code: string
          pairId: number
          /** Já dispara a cobrança PIX ao abrir (botão "Ativar" da lista). */
          autoActivate?: boolean
          /** Par já conhecido (ex.: recém-criado); evita o fetch inicial. */
          pair?: Pair
      }

const DeviceModal: React.FC<{
    state: DeviceModalState | null
    onClose: () => void
    /** Chamado ao fechar para o pai recarregar a lista (estado pode ter mudado). */
    onApply?: () => void
}> = ({ state, onClose, onApply }) => {
    const navigate = useNavigate()
    const open = state !== null
    // Estado interno: o modo pode migrar create → inspect após criar um par.
    const [inner, setInner] = useState<DeviceModalState | null>(state)
    const [pair, setPair] = useState<Pair | null>(null)
    const [loadError, setLoadError] = useState<string | null>(null)

    useEffect(() => {
        setInner(state)
        setPair(state?.mode === "inspect" ? state.pair ?? null : null)
        setLoadError(null)
    }, [state])

    const needsFetch = inner?.mode === "inspect" && !pair
    const { isFetching } = useQuery(
        [
            "deviceModal",
            inner?.mode === "inspect" ? inner.code : null,
            inner?.mode === "inspect" ? inner.pairId : null,
        ],
        async () => {
            if (inner?.mode !== "inspect") return
            const res = await api.post<CodeWithPairs[]>("/device-link/list", {
                code: inner.code,
            })
            const pairs = res.data?.[0]?.pairs ?? []
            const found =
                pairs.find((p) => p.id === inner.pairId) ?? pairs[0] ?? null
            if (!found) setLoadError("Acesso não encontrado para este código.")
            setPair(found)
        },
        { enabled: open && needsFetch }
    )

    function handleClose() {
        onApply?.()
        onClose()
    }

    function handleCreated(code: string, created: Pair) {
        setPair(created)
        setInner({ mode: "inspect", code, pairId: created.id, pair: created })
    }

    const code = inner?.mode === "inspect" ? inner.code : inner?.code

    return (
        <Modal open={open} onClose={handleClose} size="lg">
            <div className="p-6">
                {inner?.mode === "create" ? (
                    <>
                        <h4 className="mb-1 text-lg font-semibold text-text">
                            Adicionar dispositivo
                        </h4>
                        <p className="mb-5 max-w-xl text-sm text-muted">
                            Digite o código que o cliente leu na TV e o link da
                            playlist. O sistema gera a senha do acesso.
                        </p>
                        <AddDeviceForm
                            onCreated={handleCreated}
                            initialCode={inner.code}
                        />
                    </>
                ) : (
                    <>
                        <h4 className="mb-1 text-lg font-semibold text-text">
                            Dispositivo
                        </h4>
                        <p className="max-w-xl text-sm text-muted">
                            Código lido pelo cliente na TV e o acesso (senha →
                            link) selecionado.
                        </p>

                        <div className="mx-auto my-4 flex w-fit items-center justify-center gap-3 rounded-xl border border-border bg-surface px-6 py-3">
                            <MonitorSmartphone className="h-6 w-6 text-primary/60" />
                            <span className="font-mono text-3xl font-bold tracking-wider text-primary">
                                {code}
                            </span>
                            {code && (
                                <button
                                    type="button"
                                    title="Ver todos os acessos deste código na listagem"
                                    aria-label="Filtrar listagem por este código"
                                    className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted transition-colors hover:bg-elevated hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                                    onClick={() => {
                                        handleClose()
                                        navigate(
                                            `/devices?filter=${encodeURIComponent(
                                                code
                                            )}`
                                        )
                                    }}
                                >
                                    <Search size={18} />
                                </button>
                            )}
                        </div>

                        {isFetching || (needsFetch && !loadError) ? (
                            <div className="flex justify-center px-6 py-12">
                                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                            </div>
                        ) : loadError || !pair || !code ? (
                            <p className="py-6 text-center text-sm text-danger-text">
                                {loadError ?? "Acesso não encontrado."}
                            </p>
                        ) : (
                            <PairRow
                                key={pair.id}
                                code={code}
                                pair={pair}
                                onPairChange={setPair}
                                autoActivate={
                                    inner?.mode === "inspect" &&
                                    inner.autoActivate
                                }
                            />
                        )}

                        {/* Rodapé: cadastrar mais um acesso (senha→link) neste code. */}
                        {code && (
                            <div className="mt-4 flex items-center justify-end gap-3 border-t border-border pt-4">
                                <span className="text-xs text-subtle">
                                    Outra playlist neste dispositivo?
                                </span>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    leftIcon={<Plus size={14} />}
                                    onClick={() =>
                                        setInner({ mode: "create", code })
                                    }
                                >
                                    Adicionar acesso
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    )
}

export default DeviceModal
